import type { ApiResponse } from '../../shared/types';
import { detectTenantSubdomain } from './tenant';

const API_BASE = (import.meta.env.VITE_API_BASE || '').replace(/\/$/, '');
const TOKEN_KEY = 'menucloud.token';

export function apiUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${API_BASE}${normalizedPath}`;
}

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly code = 'API_ERROR',
    public readonly status = 0,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

export function getStoredToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function storeToken(token: string | null): void {
  if (token) sessionStorage.setItem(TOKEN_KEY, token);
  else sessionStorage.removeItem(TOKEN_KEY);
}

export async function apiRequest<T>(
  path: string,
  options: RequestInit & { auth?: boolean; tenant?: boolean } = {},
): Promise<T> {
  const headers = new Headers(options.headers);
  if (!headers.has('Content-Type') && options.body && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (options.auth !== false) {
    const token = getStoredToken();
    if (token) headers.set('Authorization', `Bearer ${token}`);
  }
  if (options.tenant !== false) headers.set('X-Tenant-Subdomain', detectTenantSubdomain());

  let response: Response;
  try {
    response = await fetch(apiUrl(path), { ...options, headers });
  } catch {
    throw new ApiClientError('No se pudo conectar con el servidor.', 'NETWORK_ERROR');
  }

  const payload = await response.json().catch(() => null) as ApiResponse<T> | null;
  if (!response.ok || !payload || payload.ok === false) {
    const error = payload && payload.ok === false ? payload.error : null;
    throw new ApiClientError(
      error?.message || `Solicitud fallida (${response.status}).`,
      error?.code || 'HTTP_ERROR',
      response.status,
      error?.details,
    );
  }
  return payload.data;
}

export function jsonBody(value: unknown): string {
  return JSON.stringify(value);
}
